/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import { FlyToInterpolator, type LayerProps } from '@deck.gl/core';
import type { RGBAColor } from './deckgl-types';
import { GeoJsonLayer } from '@deck.gl/layers';
import { useCallback, useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { selectedFeatureAtom } from '../Incident/FeaturePopup';
import { primaryKeyForSection } from '../Post/PostLayer';
import { viewStateAtom } from './atoms';
import { DataFilterExtension } from '@deck.gl/extensions';
import { selectedRunTimeAtom } from '../Incident/atoms';
import React from 'react';
import { selectedElevationKeyAtom, selectedElevationScaleAtom } from './atoms';
import { numberProperty } from '../utils/featureProperties';
import { percentileBandColor, percentileRanksByGroup, type RankGroupOf } from './percentileRank';

/** deck.gl's click handler, so callers do not reach for mjolnir.js themselves. */
type LayerClickHandler = NonNullable<LayerProps['onClick']>;

type Props<T> = {
  id: string;
  incidentId?: string;
  highlightId?: string | number;
  data: T[];
  extruded: boolean;
  getFillColor?: ((d: T) => RGBAColor) | RGBAColor;
  getElevation?: (d: T) => number;
  getLineColor?: ((d: T) => RGBAColor) | RGBAColor;
  getFilterValue?: (d: T) => number;
  stroked?: boolean;
  pickable?: boolean;
  visible: boolean;
  dataTransform?: () => T[] | Iterable<T>;
  primaryKey?: string;
  elevationKey?: string;
  onClick?: LayerClickHandler;
  filterable?: boolean;
  /** Names the event a cell belongs to, for percentile colouring: a cell is
   * ranked among the cells of its own event (as POST priority is ranked).
   * Omitted, every cell of the layer ranks together. */
  eventOf?: RankGroupOf;
};

export function useGridCellLayer<T extends FeatureCollection | Feature>(props: Props<T>) {
  const {
    data,
    visible,
    onClick,
    id,
    incidentId,
    pickable,
    dataTransform,
    extruded,
    stroked,
    getFillColor,
    getFilterValue,
    getLineColor,
    highlightId,
    filterable,
    eventOf,
    ...rest
  } = props;

  const [selectedFeature] = useAtom(selectedFeatureAtom);
  const setViewState = useSetAtom(viewStateAtom);
  const elevationScale = useAtomValue(selectedElevationScaleAtom);
  const primaryKey = useAtomValue(primaryKeyForSection(id));
  const selectedElevationKey = useAtomValue(selectedElevationKeyAtom);
  const range = useAtomValue(selectedRunTimeAtom(`${incidentId}-${id}`))?.time ?? 0;

  const percentileData = useMemo(() => {
    if (primaryKey == null || getFillColor != null) {
      return new Map<unknown, Map<number, number>>();
    }

    const cells: { group: unknown; value: number }[] = [];
    // Only values rank: a cell without one is not drawn (see
    // getPercentileRankFillColor), so it must not pull the bands down.
    const collect = (properties: Record<string, unknown> | null | undefined) => {
      const value = properties?.[primaryKey];
      if (typeof value === 'number') {
        cells.push({ group: eventOf == null ? null : eventOf(properties), value });
      }
    };
    for (const chunk of data) {
      if (chunk.type === 'FeatureCollection') {
        for (const f of chunk.features) {
          // @ts-expect-error
          const { lastupdatedon, LastUpdatedOn } = f.properties;
          const lastUpdatedOn = lastupdatedon ?? LastUpdatedOn;

          // lastupdatedon arrives as an ISO string from PostGIS (it was epoch
          // ms from AGOL); coerce before comparing against the epoch-ms range.
          if (
            id !== 'wow-post' &&
            lastUpdatedOn != null &&
            new Date(lastUpdatedOn).valueOf() !== range
          ) {
            continue;
          }
          collect(f.properties);
        }
      } else if (chunk.type === 'Feature') {
        collect(chunk.properties);
      }
    }
    return percentileRanksByGroup(cells);
  }, [data, primaryKey, range, getFillColor, id, eventOf]);

  const getPercentileRankFillColor = useCallback(
    (d: T): RGBAColor => {
      if (d.type === 'Feature' && visible && primaryKey) {
        const value = d.properties?.[primaryKey];
        if (value == null) {
          return [0, 0, 0, 0];
        }
        const group = eventOf == null ? null : eventOf(d.properties);
        return percentileBandColor(percentileData.get(group)?.get(value));
      }
      return [0, 0, 0, 0];
    },
    [percentileData, primaryKey, visible, eventOf]
  );

  const handleLineColor = React.useCallback(
    (d: any) => {
      if (getLineColor != null) {
        if (typeof getLineColor === 'function') {
          return getLineColor(d);
        }
        return getLineColor;
      }
      return d?.id === (props?.highlightId ?? selectedFeature?.id)
        ? [255, 255, 0, 100]
        : [0, 0, 0, 0];
    },
    [props.highlightId, selectedFeature?.id, getLineColor]
  );

  const handleClick = React.useCallback<LayerClickHandler>(
    (info, event) => {
      if (visible && info.object) {
        if (onClick) {
          onClick(info, event);
        }
        // deck.gl resolves a coordinate for every pick over the map; without one
        // there is nowhere to fly.
        if (info.coordinate) {
          const [longitude, latitude] = info.coordinate;
          setViewState(view => ({
            ...view,
            maxPitch: 85,
            longitude,
            latitude,
            transitionInterpolator: new FlyToInterpolator({ speed: 3.0 }),
            transitionDuration: 1000,
          }));
        }
      }
    },
    [visible, setViewState, onClick]
  );

  const handleElevation = React.useCallback(
    (d: Feature<Geometry, Record<string, unknown>>) =>
      numberProperty(d.properties, selectedElevationKey),
    [selectedElevationKey]
  );

  const handleFilterValue = React.useCallback(
    (f: any) => {
      if (getFilterValue) {
        return getFilterValue(f);
      } else {
        if (id !== 'wow-post') {
          return new Date(
            f.properties?.lastupdatedon ?? f.properties?.LastUpdatedOn ?? 0
          ).valueOf();
        }
      }
    },
    [getFilterValue, id]
  );

  const handleLineWidth = React.useCallback(
    (d: Feature<Geometry, Record<string, unknown>> | null): number => {
      if (d?.id === selectedFeature?.id) {
        return 100;
      }
      return 100;
    },
    [selectedFeature]
  );

  const filterProps = useMemo(
    () =>
      filterable
        ? {
            extensions: [new DataFilterExtension({ filterSize: 1 })],
            filterRange: [range ?? 0, range ?? 0],
            getFilterValue: handleFilterValue,
          }
        : {},
    [filterable, range, handleFilterValue]
  );

  const layers = React.useMemo(() => {
    return data.map((geojson, index) => {
      return new GeoJsonLayer({
        // @ts-expect-error
        id: `${id}-${index}-${geojson?.features?.length ?? 0}-layer`,
        autoHighlight: false,
        data: geojson as any,
        elevationScale: elevationScale,
        dataTransform: dataTransform as any,
        extruded,
        filled: true,
        getLineColor: handleLineColor,
        getLineWidth: handleLineWidth,
        getElevation: handleElevation,
        getFillColor: (getFillColor ?? getPercentileRankFillColor) as any,
        highlightColor: [255, 255, 0, 50],
        material: true,
        pickable: pickable ?? true,
        stroked,
        visible,
        ...filterProps,
        wireframe: true,
        ...(rest as any),
        updateTriggers: {
          onClick: visible,
          getElevation: [extruded, handleElevation],
          getFillColor: [primaryKey, highlightId],
          getLineColor: [highlightId, selectedFeature?.id],
          getLineWidth: [highlightId, selectedFeature?.id],
          elevationScale: [extruded, elevationScale],
        },
        onClick: handleClick,
      });
    });
  }, [
    data,
    dataTransform,
    handleLineWidth,
    elevationScale,
    extruded,
    handleElevation,
    getFillColor,
    getPercentileRankFillColor,
    handleClick,
    highlightId,
    id,
    pickable,
    primaryKey,
    selectedFeature?.id,
    stroked,
    visible,
    rest,
    handleLineColor,
    filterProps,
  ]);

  return layers;
}
